import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: number;
  discordId: string;
  discordUsername: string;
  isAdmin: boolean;
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [inviteRole, setInviteRole] = useState<'user' | 'admin'>('user');
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string>('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('https://basketbase-workers.bakonydraco1.workers.dev/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      setUsers(result.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateInvite = async () => {
    try {
      const response = await fetch('https://basketbase-workers.bakonydraco1.workers.dev/api/admin/users/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          role: inviteRole
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create invite');
      }

      const result = await response.json();
      
      // Store the invite URL and show it in the dialog
      setGeneratedInviteUrl(result.inviteUrl);
      
      // Try to copy to clipboard (works on desktop)
      try {
        await navigator.clipboard.writeText(result.inviteUrl);
        setSuccess(`Invite link generated and copied to clipboard! Role: ${inviteRole}`);
      } catch (err) {
        setSuccess(`Invite link generated! Role: ${inviteRole}`);
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite');
    }
  };

  const handleToggleAdmin = async (userId: number, isAdmin: boolean) => {
    try {
      const response = await fetch(`https://basketbase-workers.bakonydraco1.workers.dev/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          isAdmin: !isAdmin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      fetchUsers(); // Refresh users list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await fetch(`https://basketbase-workers.bakonydraco1.workers.dev/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      fetchUsers(); // Refresh users list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setInviteRole('user');
              setOpenDialog(true);
            }}
          >
            Add User
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setInviteRole('admin');
              setOpenDialog(true);
            }}
          >
            Add Admin
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Discord Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((userRow) => (
                <TableRow key={userRow.id}>
                  <TableCell>
                    {userRow.discordUsername}
                    {userRow.discordId === user?.discordId && (
                      <Chip 
                        label="(You)" 
                        color="secondary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={userRow.isAdmin ? 'Admin' : 'User'} 
                      color={userRow.isAdmin ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(userRow.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userRow.isAdmin}
                            onChange={() => handleToggleAdmin(userRow.id, userRow.isAdmin)}
                            disabled={userRow.discordId === user?.discordId}
                          />
                        }
                        label="Admin"
                        labelPlacement="start"
                      />
                      {userRow.discordId !== user?.discordId && (
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteUser(userRow.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Invite Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Invite Link</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create a Discord invite link. Share it with the person you want to add. 
            When they click the link while logged into Discord, they'll be automatically added with the selected role.
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Role: <strong>{inviteRole === 'admin' ? 'Admin' : 'User'}</strong>
          </Typography>
          
          {generatedInviteUrl ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Invite Link:</strong>
              </Typography>
              <Box sx={{ 
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start'
              }}>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {generatedInviteUrl}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(generatedInviteUrl);
                      setSuccess('Link copied to clipboard!');
                      setTimeout(() => setSuccess(''), 3000);
                    } catch (err) {
                      // Fallback for browsers that don't support clipboard API
                      const textArea = document.createElement('textarea');
                      textArea.value = generatedInviteUrl;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      setSuccess('Link copied to clipboard!');
                      setTimeout(() => setSuccess(''), 3000);
                    }
                  }}
                >
                  Copy
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Share this link with the person you want to add. They'll need to be logged into Discord to use it.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              • <strong>User:</strong> Can add new poll data, but cannot edit or delete existing data
              <br />
              • <strong>Admin:</strong> Can add, edit, and delete poll data, plus manage users
              <br /><br />
              Click "Generate Invite Link" to create a Discord OAuth link.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setGeneratedInviteUrl('');
          }}>Close</Button>
          <Button onClick={handleCreateInvite} variant="contained" startIcon={<AddIcon />}>
            Generate Invite Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
